import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  /**
   * Renders `label` visibly above the field, not only as its accessible name.
   * Opt-in: member screens supply their own labels from the Figma designs, so
   * defaulting this on would add a second label to every existing field.
   */
  showLabel?: boolean;
  error?: string;
}

// Resting border - the error override below always wins over this. Same
// precedence, and the same two class names, as `Input`: a field in error looked
// identical to a valid one here, which made a blocked submit read as a dead
// button.
//
// Explicit white `bg-surface-card` (the token `Select` and `Input` use) so a
// textarea inside a tinted container is not tinted (UX sheet r126). The token
// is theme-aware, so the dark theme gets its own card surface.
const TEXTAREA_BORDER = 'border-text-muted admin-dark:border-border-strong';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, showLabel = false, error, className = '', ...props },
  ref,
) {
  const errorId = useId();
  const fieldId = useId();
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1">
      {showLabel && (
        <label htmlFor={fieldId} className="text-text-primary text-p6 font-medium">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={showLabel ? fieldId : undefined}
        aria-label={label}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
        className={`w-full resize-y rounded-input border ${hasError ? 'border-red' : TEXTAREA_BORDER} px-4 py-2 bg-surface-card text-input text-text-primary placeholder:text-input-placeholder admin-dark:placeholder:text-text-muted focus:outline-none focus:border-green disabled:cursor-not-allowed disabled:border-teal/5 disabled:bg-teal/5 ${className}`.trim()}
        {...props}
      />
      {hasError && (
        <p id={errorId} role="alert" className="pl-1 text-xs text-red">
          {error}
        </p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';
